import { Text } from '@airwallex/typography';
import React, { useContext, useMemo } from 'react';
import { formatMoney } from '@/utils/formatPayment';
import { LineItemsWrapper } from '@/common/LineItems/style';
import { useTranslation } from 'react-i18next';
import { useMobile, useTabletOrMobile } from '@/hooks/useMediaQuery';
import { StoreContext } from '@/providers/storeProvider';
import { formatDate, DATE_PRECISION, DATE_STYLE } from '@/utils/formatDate';
import i18n from '@/utils/i18n';
import { LineItems as LineItemsComponent, LineItemsColumn, LineItemsDataSource } from '@airwallex/line-items';
import DiscountCell from '@/common/DiscountCell';
import { VStack } from '@airwallex/stacks';
import { useGetInvoiceCommonData } from '@/hooks/useGetInvoice';
import { formatPriceCurrency } from '@billing/billing-domain-shared';
import TaxTooltip from '@/components/TaxTooltip';
import { HeaderText, DiscountColumnTooltip, LineItemColKey, ShowMoreRow, getCommonWidth } from '@/common/LineItems';
import { useShowMoreItems } from '@/hooks/useShowMoreItems';
import { sumDiscountAmounts } from '@/utils/discount';
import { LineItemGroup } from '@/components/Invoice/types';

const createItemRow = (
  item: LineItemGroup,
  currency: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  hasDiscount: boolean
): LineItemsDataSource<LineItemColKey> => {
  const row = {
    product: (
      <VStack>
        <Text variant="body" title={item.label}>
          {item.label}
        </Text>
        {item.periodStart && item.periodEnd && (
          <Text variant="hint" color="secondary">
            {t('invoice.period', {
              start: formatDate(item.periodStart, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
              end: formatDate(item.periodEnd, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY),
            })}
          </Text>
        )}
      </VStack>
    ),
    quantity: item.totalUsageVolume,
    unitPrice:
      typeof item.unitAmountExcludeTax === 'number'
        ? formatPriceCurrency({
            amount: item.unitAmountExcludeTax ?? 0,
            currency: currency,
            showSymbol: true,
            showCode: false,
          })
        : t('invoice.priceVaries'),
    amount: formatMoney(item.receivableAmountAfterDiscount ?? 0, currency),
  } as LineItemsDataSource<LineItemColKey>;

  row.tax = (
    <TaxTooltip amount={item.totalTaxAmount} currency={currency} effectiveTaxPercent={item.effectiveTaxPercent} />
  );

  if (hasDiscount) {
    row.discount =
      typeof item.totalDiscountAmountExcludeTax === 'number'
        ? `-${formatMoney(item.totalDiscountAmountExcludeTax, currency)}`
        : '-';
  }

  return row;
};

const LineItemsV2: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, lineItemGroups } = invoiceData;
  const { currency, discounts } = invoice;
  const { hasDiscount, hasInvoiceDiscount } = useGetInvoiceCommonData(invoice);
  const isMobile = useMobile();
  const isTabletOrMobile = useTabletOrMobile();
  const hasLineItemDiscounts = useMemo(() => {
    return lineItemGroups.some((item) => sumDiscountAmounts(item.lineItemDiscounts) > 0);
  }, [lineItemGroups]);

  const {
    showAllItems,
    setShowAllItems,
    showMoreButtonShown,
    visibleItems: visibleGroups,
  } = useShowMoreItems(lineItemGroups, isMobile, isTabletOrMobile);

  const fecColumns: LineItemsColumn<LineItemColKey>[] = useMemo(() => {
    const commonWidth = getCommonWidth(hasDiscount);
    const columns: LineItemsColumn<LineItemColKey>[] = [
      {
        key: 'product',
        header: (<HeaderText testId="col-product">{t('invoice.product')}</HeaderText>) as unknown as string,
      },
      {
        key: 'quantity',
        header: (<HeaderText testId="col-quantity">{t('invoice.quantity')}</HeaderText>) as unknown as string,
        width: 60,
      } as LineItemsColumn<LineItemColKey>,
      {
        key: 'unitPrice',
        header: (
          <HeaderText exclTax testId="col-unitPrice">
            {t('invoice.unitPrice')}
          </HeaderText>
        ) as unknown as string,
        align: 'right',
        width: commonWidth,
      },
    ];

    if (hasDiscount) {
      columns.push({
        key: 'discount',
        header: (
          <HeaderText
            exclTax
            testId="col-discount"
            tooltip={
              hasInvoiceDiscount ? (
                <DiscountColumnTooltip
                  discounts={discounts}
                  hasLineItemDiscounts={hasLineItemDiscounts}
                  currency={currency}
                />
              ) : undefined
            }
          >
            {t('invoice.discounts')}
          </HeaderText>
        ) as unknown as string,
        align: 'right',
        width: commonWidth,
      });
    }

    columns.push({
      key: 'amount',
      header: (
        <HeaderText exclTax testId="col-amount">
          {t('invoice.amount')}
        </HeaderText>
      ) as unknown as string,
      align: 'right',
      width: commonWidth,
    });

    columns.push({
      key: 'tax',
      header: (<HeaderText testId="col-tax">{t('invoice.tax')}</HeaderText>) as unknown as string,
      align: 'right',
      width: commonWidth,
    });

    return columns;
  }, [t, hasDiscount, hasLineItemDiscounts, hasInvoiceDiscount, discounts, currency]);

  const fecDataSources: LineItemsDataSource<LineItemColKey>[] = useMemo(() => {
    const rows: LineItemsDataSource<LineItemColKey>[] = [];

    visibleGroups.forEach((item) => {
      const row = createItemRow(item, currency, t, hasDiscount);
      rows.push(row);

      if (item.lineItemDiscounts && item.lineItemDiscounts.length > 0) {
        item.lineItemDiscounts.forEach((discount) => {
          rows.push({
            product: <DiscountCell discount={discount} variant="label" useVStack={false} />,
          } as LineItemsDataSource<LineItemColKey>);
        });
      }
    });

    if (showMoreButtonShown)
      rows.push({
        product: <ShowMoreRow showAllItems={showAllItems} setShowAllItems={setShowAllItems} />,
      } as LineItemsDataSource<LineItemColKey>);

    return rows;
  }, [visibleGroups, currency, hasDiscount, showAllItems, setShowAllItems, showMoreButtonShown, t]);

  return (
    <LineItemsWrapper showMoreButtonShown={showMoreButtonShown} isMobile={isTabletOrMobile}>
      <LineItemsComponent isMobile={isTabletOrMobile} columns={fecColumns} dataSources={fecDataSources} />
    </LineItemsWrapper>
  );
};

export default LineItemsV2;
